import { SnapshotValue } from '@/redux/postActivity/postActivity.types';

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  projects: 'Parts',
  title: 'Title',
  description: 'Description',
  category: 'Category',
  subCategory: 'Subcategory',
  tags: 'Tags',
  difficulty: 'Difficulty',
  licenceType: 'Licence',
  builtWith: 'Built with',
  onefinityModel: 'Onefinity model',
  onefinityController: 'Controller',
  onefinitySize: 'Machine size',
  originalMachine: 'Original machine',
  supportedMachines: 'Supported machines',
  softwareUsed: 'Software',
  materialUsed: 'Material',
  isDimensionalMaterial: 'Dimensional material',
  dimensionalMaterialLinks: 'Material links',
  jobType: 'Job type',
  flipDirection: 'Flip direction',
  jobSize: 'Job size',
  units: 'Units',
  zZeroPosition: 'Z zero',
  xyDatumPosition: 'XY datum',
  laserOrEndmill: 'Laser or endmill',
  toolSetter: 'Tool setter',
  probeForZHeightCommand: 'Z probe command',
  tools: 'Tools',
};

const TOOL_LABELS: Record<string, string> = {
  bitId: 'Bit',
  brandId: 'Brand',
  toolType: 'Type',
  laserOrEndmill: 'Laser/endmill',
  toolUsed: 'Size',
  toolUnit: 'Unit',
  vBitDegree: 'V-bit angle',
  tipRadius: 'Tip radius',
  spindleSpeed: 'Spindle speed',
};

const humanize = (key: string) =>
  key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

const segmentLabel = (segments: string[], labels: Record<string, string>) => {
  const [head, ...rest] = segments;
  const label = FIELD_LABELS[head] ?? humanize(head);
  const subs = rest.map((sub) => labels[sub] ?? humanize(sub));
  return [label, ...subs].join(' · ');
};

/** "jobSize.width" → "Job size · Width"; "parts.<id>.title" → "Lid · Title". */
export const fieldLabel = (field: string, labels: Record<string, string>) => {
  const segments = field.split('.');
  if (segments[0] !== 'parts') return segmentLabel(segments, labels);
  const part = labels[segments[1]] ?? 'Part';
  return `${part} · ${segmentLabel(segments.slice(2), labels)}`;
};

const isRecord = (
  value: SnapshotValue
): value is { [key: string]: SnapshotValue } =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const scalar = (value: SnapshotValue, labels: Record<string, string>) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const text = String(value);
  return labels[text] ?? text;
};

const recordLines = (
  value: { [key: string]: SnapshotValue },
  format: (inner: SnapshotValue) => string
) =>
  Object.entries(value).map(
    ([key, inner]) => `${TOOL_LABELS[key] ?? humanize(key)}: ${format(inner)}`
  );

const listSeparator = (value: SnapshotValue[]) =>
  isRecord(value[0]) ? '\n' : ', ';

function formatPresent(
  value: SnapshotValue,
  labels: Record<string, string>,
  format: (inner: SnapshotValue) => string
): string {
  if (Array.isArray(value)) return value.map(format).join(listSeparator(value));
  if (isRecord(value)) return recordLines(value, format).join(' · ');
  return scalar(value, labels);
}

/** One readable string per value; ids become the names captured at submission. */
export function formatValue(
  value: SnapshotValue | undefined,
  labels: Record<string, string>
): string {
  if (value === undefined || value === null) return '';
  return formatPresent(value, labels, (inner) => formatValue(inner, labels));
}
